<script lang="ts" setup>
import {
  ElTableColumn,
  ElPopover,
  ElCheckboxGroup,
  ElCheckbox,
  ElDivider,
  type Options,
} from "element-plus";
import { ref, watch, type CSSProperties } from "vue";
import { useLocalStorage, useWindowSize } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { debounce } from "lodash";

const defaultPopperStyle = (): CSSProperties => ({
  maxHeight: "auto",
  overflowY: "auto",
});

const { t } = useI18n();
const popperStyle = ref<CSSProperties>(defaultPopperStyle());
const { height: windowHeight } = useWindowSize();

export type ColumnSelectionItem = {
  labelKey: string;
  key: string;
  value: boolean;
  disabled?: boolean;
};

export type ColumnSelectionGroup = {
  labelKey?: string;
  items: ColumnSelectionItem[];
};

export interface Props {
  modelValue: ColumnSelectionGroup[];
  selectionKey: string;
}

const emit = defineEmits<{
  (e: "update:model-value", value: ColumnSelectionGroup[]): void;
}>();

const props = defineProps<Props>();

const localStorage = useLocalStorage(
  props.selectionKey,
  {
    items: props.modelValue,
  },
  {
    mergeDefaults: (storedColumns, defaultColumns) => {
      storedColumns.items.map((storedGroup) => {
        if (storedGroup.labelKey) return storedGroup;

        storedGroup.items = defaultColumns.items[0].items.map((defaultItem) => {
          const storedItem = storedGroup.items.find(
            (storedItem) => defaultItem.key === storedItem.key
          );

          if (storedItem && storedItem.labelKey !== defaultItem.labelKey)
            storedItem.labelKey = defaultItem.labelKey;

          return storedItem ?? defaultItem;
        });

        return storedGroup;
      });

      return storedColumns;
    },
  }
);

const localModelValue = ref<ColumnSelectionGroup[]>(
  localStorage.value.items ?? props.modelValue
);

watch(
  () => props.modelValue,
  (value) => (localModelValue.value = value)
);

watch(
  () => localStorage.value.items,
  (value) => emit("update:model-value", value),
  { immediate: true }
);

const change = (labelKey: string, index: number) => {
  localModelValue.value[index].items.forEach((option) => {
    if (labelKey === option.labelKey) {
      option.value = !option.value;
    }
  });
  localStorage.value.items = localModelValue.value;
};

const calculatePopoverHeight = debounce((popper: HTMLElement) => {
  const { height, y } = popper.getBoundingClientRect();

  if (height + y > windowHeight.value || height + y < windowHeight.value) {
    popperStyle.value = {
      maxHeight: `${windowHeight.value - y}px`,
      overflowY: "auto",
    };
  }
}, 100);

const popperOptions = ref<Partial<Options>>({
  modifiers: [
    {
      name: "flip",
      options: {
        fallbackPlacements: [],
      },
    },
    {
      name: "resize",
      enabled: true,
      phase: "main",
      effect: (arg) => {
        const resizeObserver = new ResizeObserver(() =>
          calculatePopoverHeight(arg.state.elements.popper)
        );

        resizeObserver.observe(arg.state.elements.popper);

        return () => {
          resizeObserver.disconnect();
          popperStyle.value = defaultPopperStyle();
        };
      },
      fn: (arg) => {
        calculatePopoverHeight(arg.state.elements.popper);
      },
    },
  ],
});
</script>

<template>
  <el-table-column
    width="60px"
    fixed="right"
    class-name="column-selector"
  >
    <template #header>
      <el-popover
        placement="bottom-end"
        trigger="click"
        transition="so-zoom-in-top"
        popper-class="timelines-column-selector-popover"
        width="300"
        :visible-arrow="false"
        :show-arrow="false"
        :popper-style="popperStyle"
        :popper-options="popperOptions"
      >
        <el-checkbox-group
          v-for="(group, index) in localModelValue"
          :key="`group-${index}`"
          :model-value="
            group.items
              .filter(({ value }) => value)
              .map(({ labelKey }) => t(labelKey))
          "
          class="checkbox-group"
        >
          <el-divider
            v-if="group.labelKey && group.items.length > 0"
            content-position="left"
            class="divider"
          >
            {{ t(group.labelKey) }}
          </el-divider>

          <el-checkbox
            v-for="item in group.items"
            :key="item.key"
            :label="t(item.labelKey)"
            :disabled="item.disabled"
            @change="change(item.labelKey, index)"
          />
        </el-checkbox-group>
        <template #reference>
          <font-awesome-icon
            :icon="['fal', 'cog']"
            class="gear-icon"
          />
        </template>
      </el-popover>
    </template>
  </el-table-column>
</template>

<style lang="scss" scoped>
.gear-icon {
  cursor: pointer;
  font-size: 1rem;
  vertical-align: middle;
}

.gear-icon:focus {
  outline: none;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
}

.so-divider.divider :deep(.so-divider__text) {
  background-color: var(--so-divider-bg-within-popover);
  height: 20px;
  padding-top: 10px;
}

.checkbox-group .so-checkbox {
  min-height: 16px;
  height: auto;
  align-items: flex-start;
  margin-right: 19px;
  margin-bottom: 16px;
  white-space: unset;

  &:last-of-type {
    margin-bottom: 8px;
  }
}

.so-checkbox :deep(.so-checkbox__label) {
  line-height: 1rem;
  word-break: break-word;
  text-align: left;
}
</style>
